/**
 * Data Vault Transfer API service (ENG-2264).
 * Endpoints under /api/data-vault/transfer/
 */
import axios from 'src/utils/axios';

// --- Types ---

export interface SourceRef {
  survey_id: number;
  qit_id: number;
  answer_group_id: number | null;
}

export interface Candidate {
  display_value: string;
  raw_value: unknown;
  sources: SourceRef[];
}

export interface ChildCandidate {
  child_qit_id: number;
  child_question_label: string;
  source_survey_answer_id: number;
  display_value: string;
  raw_value: unknown;
}

export interface FileCandidate {
  file_hash: string;
  filename: string;
  pdf_file_url: string | null;
  source_survey_id: number;
  source_survey_answer_id: number;
  source_survey_file_id: number;
  children: ChildCandidate[];
}

export interface Proposal {
  qit_id: number;
  instance_index: number;
  kind: 'scalar' | 'file';
  question_label: string;
  question_type: string;
  section_id: number;
  section_name: string;
  section_order: number;
  qit_order: number;
  group_id: number | null;
  group_type: string | null;
  group_label: string | null;
  candidates: Candidate[] | null;
  is_unanimous: boolean;
  is_single_source: boolean;
  files: FileCandidate[] | null;
}

export interface Decision {
  id: number;
  qit_id: number;
  instance_index: number;
  decision: 'confirm' | 'discard' | 'pick' | 'files_selected';
  chosen_source_survey_id: number | null;
  chosen_source_qit_id: number | null;
  chosen_source_answer_group_id: number | null;
  files_payload: unknown | null;
  commit_status: 'pending' | 'committed' | 'failed';
  failure_reason: string | null;
  committed_at: string | null;
  decided_at: string;
}

export interface TransferSummary {
  total: number;
  decided: number;
  remaining: number;
}

export interface TransferState {
  status:
    | 'not_evaluated'
    | 'no_candidates'
    | 'pending'
    | 'committing'
    | 'committed';
  redirect_to: 'data-transfer' | 'questionnaire';
  transfer_id: number | null;
  proposals: Proposal[] | null;
  decisions: Decision[] | null;
  summary: TransferSummary | null;
}

export interface DecisionPayload {
  qit_id: number;
  instance_index: number;
  decision: 'confirm' | 'discard' | 'pick' | 'files_selected';
  chosen_source_survey_id?: number | null;
  chosen_source_qit_id?: number | null;
  chosen_source_answer_group_id?: number | null;
  files_payload?: unknown | null;
}

// --- API calls ---

export async function fetchTransferState(
  surveyId: number
): Promise<TransferState> {
  const { data } = await axios.get<TransferState>(
    `data-vault/transfer/survey/${surveyId}/`
  );
  return data;
}

export async function saveDecision(
  transferId: number,
  payload: DecisionPayload
): Promise<{ decision: Decision; summary: TransferSummary }> {
  const { data } = await axios.post(
    `data-vault/transfer/${transferId}/decisions/`,
    payload
  );
  return data;
}

export async function deleteDecision(
  transferId: number,
  qitId: number,
  instanceIndex: number
): Promise<void> {
  await axios.delete(
    `data-vault/transfer/${transferId}/decisions/${qitId}/${instanceIndex}/`
  );
}

export async function commitTransfer(
  transferId: number
): Promise<{ status: string }> {
  const { data } = await axios.post(
    `data-vault/transfer/${transferId}/commit/`
  );
  return data;
}
